/**
 * DASHBOARD-TOPIC.WIDGET
 * Display results for a topic
 */

import { withTranslation, WithTranslation } from "react-i18next"
import { connect } from "react-redux"
import Card from "@/components/card"
import { Question } from "@/redux/question.types"
import { Topic } from "@/redux/topic.types"
import { Axis } from "@/redux/axis.types"
import ListItem from "@/components/list-item"
import { FadeIn } from "@/utils/animations.utils"
import { sortBy } from "lodash"
import { SurveyState } from "@/redux/survey.types"
import Chip from "@/components/chip"
import { Session } from "@/redux/_session.types"
import Space from "@/components/space"
import DashboardQuestionOpenWidget from "./dashboard-question-open.widget"
import { Proposition } from "@/redux/proposition.types"
import DashboardTopicHeaderWidget from "./dashboard-topic-header.widget"
import DashboardPropositionWidget from "./dashboard-proposition.widget"
import Link from "@/components/link"
import QuestionBenchmarkModal from "@/features/benchmark/entities/question/ui/modals/QuestionBenchmarkModal"
import { useContext, useEffect, useState } from "react"
import { BenchmarkContext } from "@/features/benchmark/entities/question/model/benchmarkQuestion.context"
import DashboardEditAfterLaunchWidget from "./dashboard-edit-after-launch.widget"
import { store } from "@/index"
import { questionActivate } from "@/redux/question.actions"
import AiAssistantButton from "@/components/ai-assistant-button"
import TopicRepartition from "@/components/topic-repartition"
import PropositionBooleanRepartition from "@/components/proposition-boolean-repartition"
import TasksGenerateModal from "@/modals/tasks-generate.modal"
import { ActionPlanState, ActionPlanTask } from "@/redux/action-plan.types"
import { topicActivate } from "@/redux/topic.actions"
import FilterCreateFromQuestionModal from "@/modals/filter-create-from-question.modal"
import DashboardQuestionHarassmentWidget from "./dashboard-question-harassment.widget"
import Button from "@/components/button"
import TasksEditModal from "@/modals/tasks-edit.modal"
import { faPlus } from "@fortawesome/free-solid-svg-icons"
import DashboardBenchmarkMiniCard from "@/features/benchmark/entities/question/ui/DashboardBenchmarkMiniCard"

interface StateProps extends WithTranslation {
  _session: Session
  actionPlan: ActionPlanState
  survey: SurveyState
}

interface OwnProps {
  isSmall?: boolean
  addTasks?: boolean
  addTaskActive?: boolean
  currentAxis: Axis
  currentTopic: Topic
  isEditable?: boolean //show option to reverse question (only for supervisors)
  isOpen?: boolean
  onOpen?: Function
}

type Props = StateProps & OwnProps

const MODAL_ADD_TASK: string = "MODAL_ADD_TASK"
const MODAL_TASK_GENERATE: string = "MODAL_TASK_GENERATE"

function DashboardTopicWidget(props: Props) {
  const { t } = props
  const benchmark = useContext(BenchmarkContext)

  //For action play tab disable repartition bar
  const actionPlanTab = window.location.pathname.indexOf("/tasks/edit") > -1

  const CLICK_ASSISTANT: "MODAL_TASK" | "QUESTION_TIPS" = props._session.modules.survey.task
    ? "MODAL_TASK"
    : "QUESTION_TIPS"

  //Display option to edit the question once the survey is launch
  //For superadmin only
  const [showEditAfterLaunch, setShowEditAfterLaunch] = useState(false)
  //Level 3 supervisor: question for which the bank modal is open (create its benchmark)
  const [benchmarkCreateQuestion, setBenchmarkCreateQuestion] = useState<{ id: string; text: string } | null>(null)

  //Axis
  const [axis, setAxis] = useState<Axis>(props.currentAxis)

  //Topic
  const [topic, setTopic] = useState<Topic>(props.currentTopic)

  const [currentModal, setCurrentModal] = useState<string | null>(null)

  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null)

  const [isTasked, setIsTasked] = useState<boolean>(
    props.actionPlan.active.tasks.some((task: ActionPlanTask) => task.topicAid === topic.aid)
  )

  useEffect(() => {
    setTopic(props.currentTopic)
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.currentTopic.id, props.currentTopic.open, props.currentTopic.note])

  useEffect(() => {
    setAxis(props.currentAxis)
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.currentAxis.id, props.currentAxis.open, props.currentAxis.note])

  useEffect(() => {
    if (!isTasked) {
      setIsTasked(props.actionPlan.active.tasks.some((task: ActionPlanTask) => task.topicAid === topic.aid))
    }
  }, [props.actionPlan.active.tasks])

  //Open topic
  //Detect primary question in the list and update it if needed
  async function openTopic() {
    if (props.onOpen) {
      props.onOpen()
    }
  }

  return !topic.open && !props.isOpen ? (
    <DashboardTopicHeaderWidget
      isOpen={props.isOpen}
      onOpen={props.onOpen}
      currentTopic={topic}
      currentAxis={axis}
      addTasks={props.addTasks}
      addTaskActive={props.addTaskActive}
    />
  ) : (
    <FadeIn>
      {currentQuestion && (
        <FilterCreateFromQuestionModal currentQuestion={currentQuestion} onClose={() => setCurrentQuestion(null)} />
      )}

      {currentModal === MODAL_TASK_GENERATE && (
        <TasksGenerateModal selectedTopic={topic} onClose={() => setCurrentModal(null)} />
      )}

      {currentModal === MODAL_ADD_TASK && <TasksEditModal onClose={() => setCurrentModal(null)} />}

      {benchmarkCreateQuestion && (
        <QuestionBenchmarkModal
          questionId={benchmarkCreateQuestion.id}
          text={benchmarkCreateQuestion.text}
          onClose={() => setBenchmarkCreateQuestion(null)}
        />
      )}

      <div className="detail-topic-card" id={"details-topic-card-" + topic.id}>
        <Card isWithoutPadding exportName={topic.label}>
          <ListItem key={topic.aid} isEditable onClick={() => openTopic()}>
            <div className="flex1">
              <DashboardTopicHeaderWidget
                isOpen={props.isOpen}
                addTasks={props.addTasks}
                onOpen={props.onOpen}
                hideProgressBar={props._session.accountOptions.dashboardDisplayMode === "satisfaction"}
                currentTopic={topic}
                currentAxis={axis}
              />
            </div>
          </ListItem>

          {topic.hide ? (
            <ListItem>
              <div className="grey-t">
                {topic.usersCount +
                  " " +
                  t(topic.usersCount > 1 ? "propositions" : "proposition") +
                  ". " +
                  t("survey_no_results_help", {
                    count: props._session.participationMin
                  })}
              </div>
            </ListItem>
          ) : (
            topic.Questions.map((question, i) => {
              const showBenchmarkMiniCard =
                question.type === "scale" &&
                benchmark.benchmarkVisible &&
                question.note != null &&
                question.note > 0 &&
                (benchmark.benchmarkLoading || benchmark.benchmarksByAid[question.id] != null)

              return question.type === "open" ? (
                <DashboardQuestionOpenWidget
                  key={question.id}
                  count={question.messagesCount}
                  currentTopic={topic}
                  currentQuestion={question}
                />
              ) : (
                <ListItem key={question.id}>
                  <div className="flex1">
                    <div
                      className="flex items-center"
                      style={
                        question.type === "scale" && question.options.multipleItems ? { marginTop: 20 } : undefined
                      }>
                      {!props.isSmall && topic.Questions.length > 1 && (
                        <Chip
                          color={!question.primaryId && props.currentTopic.special !== "harassment" ? axis.color : ""}>
                          {t("question_" + (!question.primaryId ? "primary" : "secondary"))}
                        </Chip>
                      )}

                      {topic.Questions.length > 1 && <div className="width-20" />}

                      <span className={question.primaryId ? "grey-t" : ""}>{question.label}</span>

                      {!actionPlanTab &&
                        !question.primaryId &&
                        props._session.getModuleObserver("assistant") &&
                        question.type === "scale" &&
                        !question.options.multipleItems && (
                          <div style={{ minWidth: 180 }} className="flex">
                            <Space />
                            <div className="flex flex-dcol" data-tip={isTasked ? t("tasks_topic_used") : undefined}>
                              <AiAssistantButton
                                isAnimated={!isTasked}
                                isChecked={isTasked}
                                onClick={() =>
                                  CLICK_ASSISTANT === "QUESTION_TIPS"
                                    ? store.dispatch(questionActivate(new Question(question)))
                                    : setCurrentModal(MODAL_TASK_GENERATE)
                                }
                              />
                              <Space />
                            </div>
                          </div>
                        )}

                      <Space />

                      <div className="width-20" />

                      {!props.isSmall &&
                        question.type === "scale" &&
                        !question.options.multipleItems &&
                        question.usersCount > 0 && (
                        <span className="grey-t" style={{ minWidth: 90 }}>
                          {question.usersCount} {question.usersCount > 1 ? t("propositions") : t("proposition")}
                        </span>
                      )}

                      {question.type === "choice" && props._session.connectedAsSupervisor && props._session.supervisorLevel === 3 && (
                        <Link
                          onClick={() => {
                            store.dispatch(topicActivate(topic))
                            setCurrentQuestion(question)
                          }}
                          isWithoutMargin>
                          Créer un filtre (gestionnaire IDTree uniquement)
                        </Link>
                      )}
                    </div>

                    {
                      /** show text if trigger */
                      question.getTrigger(question.getQuestionPrimary(topic.Questions)) && question.primaryId && (
                        <div className="details-question-trigger grey-t">
                          <i>
                            {t("question_answer_at_alt")}&nbsp;
                            <b>
                              {question.getTriggerLabel(
                                props.survey.active.template,
                                question.getQuestionPrimary(topic.Questions),
                                props._session.language
                              )}
                            </b>
                            &nbsp;
                            {t("question_answer_at_question_name", {
                              question: question.getQuestionPrimary(topic.Questions).label
                            })}
                          </i>
                        </div>
                      )
                    }

                    {question.usersCount === 0 && question.type === "scale" && !question.options.multipleItems && (
                      <p style={{ margin: "22px 0px" }} className="grey-t">
                        <b>
                          {t("questions_no_results", {
                            count: props._session.participationMin
                          })}
                        </b>
                      </p>
                    )}

                    {question.type === "choice" && (
                      <div className="flex">
                        <Space />
                        <div className="details-proposition-container">
                          {sortBy(question.Propositions, "count")
                            .reverse()
                            .map((proposition) => (
                              <DashboardPropositionWidget
                                key={proposition.id}
                                currentAxis={axis}
                                currentProposition={proposition}
                              />
                            ))}

                          {question.withMessage && (
                            <div>
                              <DashboardPropositionWidget
                                currentAxis={axis}
                                currentProposition={new Proposition({ id: "others", count: question.messagesCount })}
                              />

                              <div className="height-20" />

                              {question.Messages.length > 0 && props._session.isTabDisplayed("/dashboard/messages") && (
                                <p>
                                  <b>
                                    {t("utils_details")} ({t("utils_others")})
                                  </b>
                                </p>
                              )}

                              <div className="height-20" />

                              <DashboardQuestionOpenWidget
                                key={question.id}
                                count={question.messagesCount}
                                currentTopic={topic}
                                currentQuestion={question}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {question.type === "scale" && question.options.multipleItems && (
                      <div className="flex">
                        <Space />
                        <div
                          className="details-proposition-container"
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr auto auto auto",
                            marginTop: 20,
                            marginBottom: 20,
                            rowGap: 16,
                            columnGap: 10,
                            alignItems: "center"
                          }}>
                          {question.Propositions.map((proposition) => (
                            <div key={proposition.id} style={{ display: "contents" }}>
                              <div className="grey-t">{proposition.label}</div>
                              <PropositionBooleanRepartition
                                repartition={proposition.repartition}
                                noAnswers={proposition.noAnswers}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {question.type !== "choice" &&
                      !question.options.multipleItems &&
                      question.repartition.length > 0 && (
                        <TopicRepartition
                          displayWidth={props.isSmall ? 10 : undefined}
                          hideLabels={props.isSmall}
                          id={question.id}
                          repartition={question.repartition}
                          noAnswers={question.noAnswers}
                          reverse={question.reverse}
                          isLarge={i === 0}
                        />
                      )}

                    {props.currentTopic.countHarassment > 0 && question.type === "scale" && (
                      <DashboardQuestionHarassmentWidget currentQuestion={question} />
                    )}

                    {showBenchmarkMiniCard ||
                    (props.isEditable && props._session.connectedAsSupervisor && question.type === "scale") ? (
                      <div className="flex items-end gap-3 mt-2">
                        {showBenchmarkMiniCard &&
                          (benchmark.benchmarkLoading ? (
                            <DashboardBenchmarkMiniCard
                              isLoading
                              accountOptions={props._session.dashboardDisplaySettings}
                              score={0}
                              onOpen={() => {}}
                            />
                          ) : (
                            <DashboardBenchmarkMiniCard
                              accountOptions={props._session.dashboardDisplaySettings}
                              score={(question.note ?? 0) / 10}
                              benchmarkValue={benchmark.benchmarksByAid[question.id] != null ? benchmark.benchmarksByAid[question.id].value / 10 : null}
                              onOpen={() =>
                                benchmark.openDetail!(
                                  question.note!,
                                  axis.color,
                                  benchmark.benchmarksByAid[question.id]?.canonicalAid ?? question.id,
                                  benchmark.benchmarksByAid[question.id] ?? null
                                )
                              }
                            />
                          ))}
                        {/* Left, in the slot of the benchmark mini card this question does not have */}
                        {props.isEditable &&
                          props._session.connectedAsSupervisor &&
                          props._session.supervisorLevel === 3 &&
                          question.type === "scale" &&
                          benchmark.benchmarksByAid[question.id] == null && (
                            <Link
                              isWithoutMargin
                              onClick={() => setBenchmarkCreateQuestion({ id: question.id, text: question.name?.fr || question.label })}>
                              <span style={{ fontSize: 12 }}>Créer le benchmark (Gestionnaire IDTree uniquement)</span>
                            </Link>
                          )}
                        {props.isEditable && props._session.connectedAsSupervisor && props._session.supervisorLevel === 3 && question.type === "scale" && (
                          <div className="flex ml-auto">
                            <Link isWithoutMargin onClick={() => setShowEditAfterLaunch(!showEditAfterLaunch)}>
                              <span style={{ fontSize: 12 }}>
                                Modifier après enquête (Gestionnaire IDTree uniquement)
                              </span>
                            </Link>
                          </div>
                        )}
                      </div>
                    ) : null}

                    {showEditAfterLaunch && <DashboardEditAfterLaunchWidget currentQuestion={question} />}
                  </div>
                </ListItem>
              )
            })
          )}

          {props.addTasks && (
            <div className="flex m-20 -mt-10">
              <Space />
              <Button isFullWidth onClick={() => setCurrentModal(MODAL_ADD_TASK)} className="primary" icon={faPlus}>
                {t("tasks_list_add")}
              </Button>
            </div>
          )}
        </Card>
      </div>
    </FadeIn>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  actionPlan: state.actionPlan,
  survey: state.survey
})

export default connect(mapStateToProps)(withTranslation()(DashboardTopicWidget))
